import { useCallback, useEffect, useMemo, useState } from "react";

import { get as apiGet } from "../../../services/middleware/api.middleware";
import { useAuth } from "../../../Contexts/AuthContext";

import type {
  CanvasPosition,
  CBCRelation,
  EnsembleCBC,
} from "../types/ensemble.types";

interface UseEnsembleDataResult {
  cbcs: EnsembleCBC[];
  relations: CBCRelation[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const parseCBCs = (payload: unknown): EnsembleCBC[] => {
  if (!Array.isArray(payload)) return [];
  return payload
    .map((entry) => {
      const record = entry as Partial<EnsembleCBC>;
      if (
        typeof record.ensemble_model_cbc_id !== "number" ||
        typeof record.ensemble_model_cbc_name !== "string"
      ) {
        return null;
      }
      const coords = (record.canvas_position ?? {}) as Partial<CanvasPosition>;
      const position =
        typeof coords === "object" &&
        typeof coords.x === "number" &&
        typeof coords.y === "number"
          ? { x: coords.x, y: coords.y }
          : { x: 0, y: 0 };

      return {
        ensemble_model_cbc_id: record.ensemble_model_cbc_id,
        ensemble_model_cbc_name: record.ensemble_model_cbc_name,
        ensemble_model_cbc_type: (record.ensemble_model_cbc_type ??
          "person") as EnsembleCBC["ensemble_model_cbc_type"],
        ensemble_name: record.ensemble_name ?? "",
        business_keys: record.business_keys ?? "",
        description: record.description ?? "",
        canvas_position: position,
      };
    })
    .filter(Boolean) as EnsembleCBC[];
};

export const parseRelations = (payload: unknown): CBCRelation[] => {
  if (!Array.isArray(payload)) return [];
  return payload
    .map((entry) => {
      const record = entry as Partial<CBCRelation>;
      if (
        typeof record.ensemble_model_cbc_relation_id !== "number" ||
        typeof record.src_ensemble_model_cbc_id !== "number" ||
        typeof record.tgt_ensemble_model_cbc_id !== "number"
      ) {
        return null;
      }
      const relationDescription =
        typeof (record as any).relation_description === "string"
          ? ((record as any).relation_description as string)
          : typeof (record as any).relation_details === "string"
          ? ((record as any).relation_details as string)
          : "";
      return {
        ensemble_model_cbc_relation_id:
          record.ensemble_model_cbc_relation_id,
        src_ensemble_model_cbc_id: record.src_ensemble_model_cbc_id,
        tgt_ensemble_model_cbc_id: record.tgt_ensemble_model_cbc_id,
        relation_description: relationDescription,
      };
    })
    .filter(Boolean) as CBCRelation[];
};

export const useEnsembleData = (
  envId: number,
  modelId: number,
  options?: { enabled?: boolean }
): UseEnsembleDataResult => {
  const [cbcs, setCBCs] = useState<EnsembleCBC[]>([]);
  const [relations, setRelations] = useState<CBCRelation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { tokens } = useAuth();

  const enabled = options?.enabled ?? true;

  const endpoints = useMemo(() => {
    if (
      !Number.isFinite(envId) ||
      envId <= 0 ||
      !Number.isFinite(modelId) ||
      modelId <= 0
    ) {
      return null;
    }
    const base = `environment/${envId}/ensemble_model/${modelId}`;
    return {
      cbcs: `${base}/ensemble_model_cbc`,
      relationsAbsolute: `${process.env.REACT_APP_API_URL}${base}/ensemble_model_cbc_relation`,
    };
  }, [envId, modelId]);

  const fetchData = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    if (!endpoints) {
      setCBCs([]);
      setRelations([]);
      setLoading(false);
      setError("Missing or invalid ensemble identifiers.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (!tokens.accessToken) {
        setLoading(false);
        setError("Missing access token. Please sign in again.");
        return;
      }

      const headers = {
        Authorization: `Bearer ${tokens.accessToken}`,
      };

      const [cbcResponse, relationsResponse] = await Promise.all([
        apiGet<unknown>(endpoints.cbcs, headers),
        apiGet<unknown>(endpoints.relationsAbsolute, headers, {
          options: { isUrlExclusive: true },
        }),
      ]);

      setCBCs(parseCBCs(cbcResponse));
      setRelations(parseRelations(relationsResponse));
    } catch (err) {
      const message =
        typeof err === "string"
          ? err
          : err instanceof Error
          ? err.message
          : "Failed to load ensemble modelling data.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [enabled, endpoints, tokens.accessToken]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  return {
    cbcs,
    relations,
    loading,
    error,
    refetch: () => fetchData(),
  };
};

export default useEnsembleData;
